import { Component, EventEmitter, OnInit, ViewChild, signal } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { ActionBarGroup } from '@common/components/action-bar/action-bar.interface';
import { FormProperty } from '@common/components/app-control/app-control.component';
import { AppGridComponent } from '@common/components/app-grid/app-grid.component';
import { ColumnSettings } from '@common/interfaces/column-settings.interface';
import { CommandParams } from '@common/interfaces/command-params.interface';
import { GridSettings } from '@common/interfaces/grid-settings.interface';
import { User } from '@common/models/user';
import { CommonService } from '@common/services/common.service';
import { DialogService } from '@common/services/dialog.service';
import { RememberStateService } from '@common/services/remember-state.service';
import { ToastrNotificationService } from '@common/services/toastr-notification.service';
import { UserService } from '@common/services/user.service';
import { environment } from '@environments/environment';
import { SignalQueryResult } from '@ngneat/query';
import { TranslateService } from '@ngx-translate/core';
import { DialogCloseResult, DialogSettings } from '@progress/kendo-angular-dialog';
import { GridComponent } from '@progress/kendo-angular-grid';
import { DrawerComponent, SelectEvent } from '@progress/kendo-angular-layout';
import _ from 'lodash';
import { ExportType } from './export-type';

@Component({ template: '' })
export abstract class BaseListComponent<TQuery = any> implements OnInit {
    @ViewChild(AppGridComponent) public appGrid: AppGridComponent;
    @ViewChild('drawer') drawer: DrawerComponent;

    abstract columns: ColumnSettings[];
    abstract queryName: string;

    protected router: Router;
    protected userService: UserService;

    protected toastrNotificationService: ToastrNotificationService;
    protected translateService: TranslateService;
    protected dialogService: DialogService;
    protected rememberStateService: RememberStateService;

    protected exportPDFCommand: string;
    protected exportExcelCommand: string;
    protected exportCSVCommand: string;
    protected exportCommandData: any;

    protected parentRoute = window.location.pathname.split('/').slice(0, -1).join('/');
    protected defaultViewMode = environment.settings.view.defaultViewMode;
    protected actionBar: ActionBarGroup[];
    protected query: SignalQueryResult<TQuery, Error> = signal(null);
    protected user: User;
    protected isBusy = false;
    protected filterOpen = false;
    protected filter: any;
    /**
     * List of ignored filters that should not be counted as active filters
     * This can be overridden in the child class if needed
     */
    protected ignoredActiveFilters = [
        'id',
        'skip',
        'take',
        'orderBy',
        'orderDirection',
        'orderByList',
        'orderDirectionList',
        'sourceIds',
        'activeTab'
    ];
    protected createPermission: string;
    protected selection = [];
    /**
     * @deprecated
     * Here until pages are refactored to use new NavigationService. This will be removed in the future.
     */
    protected breadcrumbs = null;
    /**
     * @deprecated
     * Here until pages are refactored to use new NavigationService. This will be removed in the future.
     */
    protected currentNavItem = null;
    protected activeTab = 0;

    private persistFilter = environment.settings.list.persistFilter;
    private rememberFilterState = environment.settings.list.rememberFilterState;

    filtersChanged = new EventEmitter<any[]>();

    constructor(protected commonService: CommonService) {
        this.toastrNotificationService = commonService.toastrNotificationService;
        this.router = commonService.router;
        this.userService = commonService.userService;
        this.translateService = commonService.translateService;
        this.dialogService = commonService.dialogService;
        this.rememberStateService = commonService.rememberStateService;

        this.search = _.debounce(this.search.bind(this), 50);
        this.user = this.userService.currentUserSubject.getValue();

        if (this.rememberFilterState)
            this.filterOpen = this.rememberStateService.get<boolean>(`filterExpanded:${this.router.url}`) || false;

        this.actionBar = [
            {
                label: 'Actions',
                items: [
                    {
                        tooltip: 'New',
                        icon: 'faSolidPlus',
                        iconOnly: true,
                        variant: 'primary',
                        isVisible: () => this.canCreateNew(),
                        onClick: () => this.createNew(),
                        isDisabled: () => this.disableInsertNewBtn()
                    }
                ]
            },
            {
                label: 'Export',
                items: [
                    {
                        tooltip: 'Excel',
                        icon: 'faSolidFileExcel',
                        iconOnly: true,
                        variant: 'excel',
                        isVisible: () => this.canExportExcel(),
                        onClick: () =>
                            this.commonService.exportService.export(
                                ExportType.Excel,
                                this.exportExcelCommand,
                                this.selection.length === 0,
                                this.appGrid,
                                this.filter,
                                this.selection,
                                this.exportCommandData
                            )
                    },
                    {
                        tooltip: 'Csv',
                        icon: 'faSolidFileCsv',
                        iconOnly: true,
                        variant: 'csv',
                        isVisible: () => this.canExportCSV(),
                        onClick: () =>
                            this.commonService.exportService.export(
                                ExportType.Csv,
                                this.exportCSVCommand,
                                this.selection.length === 0,
                                this.appGrid,
                                this.filter,
                                this.selection,
                                this.exportCommandData
                            )
                    },
                    {
                        tooltip: 'Pdf',
                        icon: 'faSolidFilePdf',
                        iconOnly: true,
                        variant: 'pdf',
                        isVisible: () => this.canExportPDF(),
                        onClick: () =>
                            this.commonService.exportService.export(
                                ExportType.Pdf,
                                this.exportPDFCommand,
                                this.selection.length === 0,
                                this.appGrid,
                                this.filter,
                                this.selection,
                                this.exportCommandData
                            )
                    }
                ]
            }
        ];
    }

    get nrOfActiveFilters(): number {
        const filter = this.getFilter();
        return Object.entries(filter).reduce((acc, [key, value]) => {
            if (this.shouldIgnoreFilter(key, value)) return acc;
            return acc + 1;
        }, 0);
    }

    get listOfActiveFilters(): { label: string; value: string }[] {
        const { _skip, _take, _orderBy, _orderDirection, ...filters } = this.getFilter();
        return Object.entries(filters)
            ?.filter(([_label, value]) => value)
            .map(([label, value]) => ({ label, value: value?.toString() }));
    }

    ngOnInit() {
        this.initialize();
    }

    protected initialize() {
        this.filter = this.getFilter();
        if (this.filter['activeTab']) this.activeTab = this.filter['activeTab'];
        this.search();
    }

    protected search(clearSelection = false, customGrid = null) {
        if (clearSelection) this.selection = [];

        const grid = customGrid || (this.appGrid?.grid as GridComponent);
        const { pageSize, skip } = grid ?? { pageSize: 10, skip: 0 };
        const sortDescriptors = (grid?.sort?.length ? grid.sort : environment.settings.grid.sort) ?? [];
        const { field, dir } = sortDescriptors.at(0) || {};
        const orderByList: string[] = [];
        const orderDirectionList: string[] = [];

        for (const sortItem of sortDescriptors) {
            if (!sortItem?.field) continue;
            orderByList.push(sortItem.field);
            orderDirectionList.push(sortItem.dir ?? null);
        }

        this.filter = {
            ...this.filter,
            skip,
            take: pageSize,
            orderBy: field,
            orderDirection: dir,
            orderByList,
            orderDirectionList
        };
        this.saveFilter();
        this.query = this.queryListData().result;
        //Prefetch next page
        this.prefetchListData({ ...this.filter, skip: skip + pageSize, take: pageSize });
    }

    protected async onTabSelect(event: SelectEvent): Promise<void> {
        this.activeTab = +event.index;
        this.saveFilter();

        // Clear the query cache to prevent stale data from showing during tab switch
        this.commonService.queryService.queryClient.removeQueries({
            predicate: (query) => query.queryKey.some((key: any) => key === this.queryName)
        });

        await this.router.navigate([], {
            relativeTo: this.commonService.activatedRoute,
            queryParams: { tab: this.activeTab > 0 ? +event.index : null },
            ...this.getNavigationExtras()
        });

        this.filter = this.getFilter();

        this.search(true);
    }

    protected canCreateNew() {
        if (this.createPermission) return this.user?.hasPermission(this.createPermission);
        return true;
    }

    protected disableInsertNewBtn() {
        return false;
    }

    protected createNew() {
        this.router.navigate([`${this.parentRoute}/create/`]);
    }

    protected actionHasPermission(permission) {
        return this.user?.hasPermission(permission);
    }

    protected canExportPDF() {
        return !!this.exportPDFCommand;
    }

    protected canExportExcel() {
        return !!this.exportExcelCommand;
    }

    protected canExportCSV() {
        return !!this.exportCSVCommand;
    }

    protected getDefaultFilter() {
        return {};
    }

    /**
     * Function to get the base name of the query.
     * If the last character of the query name is 's', it removes the 's' from the end.
     *
     * @returns The base name of the query, e.g. 'ServiceRequests' -> 'ServiceRequest'
     */
    protected getBaseName(): string {
        return this.queryName[this.queryName.length - 1] === 's' ? this.queryName.slice(0, -1) : this.queryName;
    }

    /**
     * Function to get the processed base name of the query.
     * It splits the base name from {@link getBaseName} by capital letters.
     *
     * @returns The processed base name of the query, e.g. 'ServiceRequest' -> 'Service Request'
     */
    protected getProcessedBaseName(): string {
        const baseName = this.getBaseName();
        return baseName.replace(/([A-Z])/g, ' $1').trim();
    }

    protected clearFilter(name?: string) {
        this.commonService.queryService.queryClient.removeQueries({
            predicate: (query) => query.queryKey.some((key: any) => key === this.queryName)
        });

        if (name) {
            this.filter[name] = null;
        } else {
            this.filter = this.getDefaultFilter();
        }
        this.search();
    }

    protected async filterDialog(filterProperties: FormProperty[], dialogSettings: DialogSettings = { width: '70%' }) {
        const data = await this.dialogService.form({
            options: {
                title: 'Filter',
                cancelAction: () => this.clearFilter(),
                cancelText: 'Clear',
                cancelIcon: 'faSolidFilterCircleXmark',
                confirmText: 'Filter',
                confirmIcon: 'faSolidFilter',
                model: this.filter,
                properties: filterProperties
            },
            dialogSettings
        });
        if (data instanceof DialogCloseResult || typeof data !== 'object') return;
        this.filter = data;
        this.search();
        this.resetPaging();
    }

    protected toggleFilter() {
        this.filterOpen = !this.filterOpen;
    }

    protected getPersistedFilter() {
        if (this.persistFilter === false) return {};

        try {
            const filter = this.rememberStateService.get<string>(`filter:${this.router.url}`);
            return JSON.parse(filter);
        } catch (error) {
            return {};
        }
    }

    protected saveGridSettings(settings?: { value: GridSettings; filter?: any }) {
        const saveModel = {
            key: this.commonService.getSanitizedUrl(settings.filter),
            value: JSON.stringify(settings.value)
        };

        this.commonService.queryService
            .getCommandMutation()
            .mutateAsync({
                command: 'SaveGridSetting',
                data: { saveModel },
                invalidate: 'gridSettings'
            })
            .catch((err) => {
                this.toastrNotificationService.show({ type: 'error', message: 'Error saving grid settings' });
                return err;
            });
    }

    protected deleteGridSettings(filter?: any) {
        this.commonService.queryService
            .getCommandMutation()
            .mutateAsync({
                command: 'DeleteGridSetting',
                data: { settingKey: this.commonService.getSanitizedUrl(filter) },
                invalidate: 'gridSettings'
            })
            .catch((err) => {
                this.toastrNotificationService.show({ type: 'error', message: 'Error clearing grid settings' });
                return err;
            });
    }

    protected onSelectedKeysChange(keys: any[]) {
        this.selection = keys;
    }

    /**
     * Executes a command with the provided parameters.
     *
     * @param params - The parameters for the command execution.
     * @param params.command - The command to be executed.
     * @param params.data - Optional data to be sent with the command. Defaults to an empty object.
     * @param params.invalidate - Optional query name to invalidate. Defaults to the current query name.
     * @param params.customCallback - Optional custom callback function to be executed after the command. Defaults to clearing the selection.
     * @param params.showSpinner - Optional flag to show a spinner during the command execution.
     * @returns A promise that resolves with the result of the command execution.
     */
    protected executeCommand(params: CommandParams): Promise<any> {
        const {
            command,
            data = {},
            invalidate = this.queryName,
            customCallback = () => (this.selection = []),
            showSpinner
        } = params;
        const ids = this.appGrid.selectBy === 'row' ? this.selection.map((x) => x.id) : this.selection;

        return this.commonService.executeCommand({
            command,
            data: { ids, ...data },
            customCallback,
            invalidate,
            showSpinner
        });
    }

    /**
     * Executes a workflow command with the provided parameters.
     *
     * @param params - The parameters for the command execution.
     * @param params.command - The command to be executed.
     * @param params.data - Additional data to be sent with the command. Defaults to an empty object.
     * @param params.customCallback - A custom callback function to be executed after the command. Defaults to a function that clears the selection.
     * @param params.invalidate - The query name or an array of query names to be invalidated. Defaults to the query name of the current instance.
     * @param params.baseName - The base name of the entity. Defaults to the result of `getBaseName()` method.
     * @param params.showSpinner - A flag indicating whether to show a spinner during the command execution.
     * @returns A promise that resolves when the command execution is complete.
     */
    protected executeWorkflowCommand(params: CommandParams): Promise<any> {
        const {
            command,
            data = {},
            customCallback = () => {
                this.selection = [];
            },
            invalidate = this.queryName,
            baseName = this.getBaseName(),
            showSpinner
        } = params;

        const ids = this.appGrid.selectBy === 'row' ? this.selection.map((x) => x.id) : this.selection;
        const stateFromCode = this.appGrid.selectBy === 'row' ? this.selection?.[0]?.statusId : null;

        return this.commonService.executeWorkflowCommand({
            command,
            data: { ids, entityType: baseName, stateFromCode, ...data },
            customCallback,
            invalidate,
            showSpinner
        });
    }

    private queryListData() {
        return this.commonService.queryService.getQuery<TQuery>(this.queryName, this.filter, {
            injector: this.commonService.injector,
            refetchOnWindowFocus: true
        });
    }

    private prefetchListData(customFilter = null) {
        return this.commonService.queryService.prefetch(this.queryName, customFilter || this.filter);
    }

    private getFilter() {
        const persistedFilter = this.getPersistedFilter();
        const filter = this.getDefaultFilter();
        return this.persistFilter && _.isObject(filter) ? { ...filter, ...persistedFilter } : filter;
    }

    private saveFilter() {
        this.rememberStateService.set(`filter:${this.router.url}`, JSON.stringify(this.filter));
    }

    private resetPaging() {
        this.appGrid.onDataStateChanged({
            skip: 0,
            take: this.appGrid?.grid?.pageSize || environment.settings.grid.pageSize
        });
    }

    private shouldIgnoreFilter(key: string, value: unknown): boolean {
        return (
            this.ignoredActiveFilters.includes(key) ||
            value === null ||
            value === undefined ||
            (Array.isArray(value) && value.length === 0) ||
            (typeof value === 'string' && value.trim() === '') ||
            (typeof value === 'number' && isNaN(value))
        );
    }

    private getNavigationExtras(): NavigationExtras {
        return { queryParamsHandling: 'merge', onSameUrlNavigation: 'reload', replaceUrl: true };
    }
}
